import React, { FC, useId } from "react";
import {
	Box,
	Typography,
	Button,
	IconButton,
	TextField,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Switch,
	Divider,
	Alert,
	alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
	RewardEngineOption,
	TreatmentSegment,
	CmVisitScheduleOption,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import {
	appendSegment,
	clearOverride,
	hasSegments,
	isOverrideActive,
	removeSegment,
	inheritHintPrefix,
	resetLadderHint,
	resolveResetLadderValue,
	rewardEngineName,
	segmentHeaderSummary,
	setOverride,
	updateSegment,
	validateSegmentContiguity,
	SegmentOverrideField,
	isNewWeekControlAvailable,
	resolveNewWeekValue,
	setNewWeekFlag,
	NEW_WEEK_CONTROL_TESTID,
	NEW_WEEK_CONTROL_LABEL,
	NEW_WEEK_CONTROL_HINT,
	NO_SEGMENTS_MESSAGE,
} from "./segmentEditor.helpers";

/**
 * SIMS-7151 (FR-3) + SIMS-7159 (FR-4, FR-6) — plan-level segment accordion editor for the
 * Treatment Plan template form.
 *
 * Structural fields (week range, visits/week, min hours between visits) are always editable (7151).
 * Per SIMS-7159 each segment additionally exposes toggle-driven overrides (reward engine, behavior,
 * visit schedule, disenrollment threshold): OFF inherits the template default (shown as a read-only
 * hint, nothing persisted); ON persists the override to segments[n]. resetLadderOnEntry is locked to
 * true when the segment's effective reward engine differs from the previous segment's (a reward-
 * engine change resets the ladder, FR-6), otherwise it is freely configurable. Contiguity validation
 * is SIMS-7167. Controlled: `segments` in / `onChange` out.
 *
 * SIMS-9184 (FR-1, FR-2, FR-20) adds "Start the next segment in a new week" above the overrides
 * divider. It is NOT an override — there is no template-level default to inherit — so it carries a
 * plain explanatory caption rather than an inherit hint, and it is rendered only on a segment that
 * has a successor.
 *
 * This is a component index (excluded from unit-coverage by jest.config); the testable derivation
 * logic lives in ./segmentEditor.helpers.ts and is jest-tested. Synthetic data only — no PHI.
 */
export interface SegmentTemplateDefaults {
	rewardEngineId?: string;
	behavior?: string;
	cmVisitScheduleOptionIds?: string;
	disenrollmentDaysThreshold?: number;
}

interface SegmentEditorProps {
	segments: TreatmentSegment[];
	onChange: (segments: TreatmentSegment[]) => void;
	rewardEngineOptions: RewardEngineOption[];
	behaviors: Event[];
	cmVisitScheduleOptions: CmVisitScheduleOption[];
	/** Template defaults — the effective values a segment inherits when its override is OFF. */
	templateDefaults?: SegmentTemplateDefaults;
	/**
	 * SIMS-9225: inside an additional activity the block's own "Program Segments" heading sits above
	 * the own-segments band, so the editor's heading is hidden there to avoid showing it twice.
	 */
	hideHeading?: boolean;
	/**
	 * SIMS-9226 (Q-6 = A): set — even to "" — when the editor edits an additional activity's own
	 * list. A segment there inherits the ACTIVITY's values, so the off-switch hints name it
	 * ("Inherits from {activity}"). Left undefined in the template's editor, which keeps the build
	 * string "Inherits template default".
	 */
	activityName?: string;
	/**
	 * SIMS-9231 AC4: save-time notices by segment index — a threshold set in this edit that will not
	 * take effect. Drawn as an informational alert at the top of the segment, which opens to show it.
	 */
	inertThresholdNotices?: Record<number, string>;
}

const SegmentEditor: FC<SegmentEditorProps> = ({
	segments,
	onChange,
	rewardEngineOptions,
	behaviors,
	cmVisitScheduleOptions,
	templateDefaults = {},
	hideHeading = false,
	activityName,
	inertThresholdNotices = {},
}) => {
	// Unique per editor: two editors share the page once an activity has its own list.
	const editorId = useId();
	const addSegment = (): void => onChange(appendSegment(segments));

	const patchNumber = (
		index: number,
		field: "visitsPerWeek" | "minHoursBetweenVisits",
		raw: string,
	): void => {
		const value = raw === "" ? 0 : Number(raw);
		onChange(updateSegment(segments, index, { [field]: value }));
	};

	const patchWeek = (
		index: number,
		edge: "start" | "end",
		raw: string,
	): void => {
		const value = raw === "" ? 0 : Number(raw);
		const current = segments[index].weekRange || { start: 0, end: 0 };
		onChange(
			updateSegment(segments, index, {
				weekRange: { ...current, [edge]: value },
			}),
		);
	};

	// Toggle an override ON (seed with the template default so it starts valid) or OFF (inherit).
	const toggleOverride = (
		index: number,
		field: SegmentOverrideField,
		on: boolean,
	): void => {
		if (!on) {
			onChange(clearOverride(segments, index, field));
			return;
		}
		const seed =
			field === "disenrollmentDaysThreshold"
				? (templateDefaults.disenrollmentDaysThreshold ?? 0)
				: (templateDefaults[field] ?? "");
		onChange(setOverride(segments, index, field, seed));
	};

	const behaviorName = (id?: string): string =>
		behaviors.find((b) => b._id === id)?.name ?? "None";

	const scheduleDisplay = (ids?: string): string => {
		if (!ids) {
			return "None";
		}
		return ids
			.split(",")
			.filter(Boolean)
			.map(
				(id) =>
					cmVisitScheduleOptions.find(
						(o) => o.cmVisitScheduleOptions.toString() === id,
					)?.cmVisitScheduleOptionsDaysDisplay ?? id,
			)
			.join(", ");
	};

	// A single toggle-driven override row: header switch + (when ON) the input, else the inherited hint.
	const overrideRow = (
		index: number,
		field: SegmentOverrideField,
		label: string,
		hint: string,
		input: React.ReactNode,
	): React.ReactNode => {
		const active = isOverrideActive(segments[index], field);
		return (
			<Grid item xs={12} md={6} key={field}>
				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={active}
							onChange={(e) => toggleOverride(index, field, e.target.checked)}
						/>
					}
					label={`Override ${label}`}
				/>
				{active ? (
					input
				) : (
					<Typography variant="caption" color="text.secondary" display="block">
						{inheritHintPrefix(activityName)}: {hint}
					</Typography>
				)}
			</Grid>
		);
	};

	// SIMS-7167: the first contiguity violation across the whole list. Resolved once
	// per render rather than inside the per-segment map (SIMS-7487 tidy-up: the call
	// scans every segment, so calling it per accordion made rendering O(N^2)).
	const contiguityError = validateSegmentContiguity(segments);

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
			data-testid="segment-editor"
		>
			{!hideHeading && (
				<Typography variant="subtitle1" fontWeight="bold">
					Program Segments
				</Typography>
			)}

			{!hasSegments(segments) ? (
				<Typography variant="body2" color="text.secondary">
					{NO_SEGMENTS_MESSAGE}
				</Typography>
			) : (
				segments.map((segment, index) => {
					// SIMS-7167: surface the first contiguity violation inline on the offending segment.
					const hasContiguityError = contiguityError?.index === index;
					// SIMS-9226 AC5: the lock and its reason (or the free switch's hint), shown persistently.
					const resetHint = resetLadderHint(
						segments,
						index,
						templateDefaults.rewardEngineId,
						rewardEngineOptions,
					);
					const resetLocked = resetHint.locked;
					const resetHintId = `${editorId}-segment-${index + 1}-reset-ladder-hint`;
					const inertNotice = inertThresholdNotices[index];
					const resetValue = resolveResetLadderValue(
						segments,
						index,
						templateDefaults.rewardEngineId,
					);
					return (
						// A notice re-keys the segment so it opens to show it (it stays collapsible).
						<Accordion
							key={inertNotice ? `${index}-inert-threshold` : index}
							defaultExpanded={!!inertNotice}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography sx={{ flexGrow: 1 }}>
									{segmentHeaderSummary(
										segment,
										index,
										rewardEngineOptions,
										templateDefaults.rewardEngineId,
									)}
								</Typography>
								<IconButton
									size="small"
									aria-label={`Remove segment ${index + 1}`}
									component="span"
									onClick={(e) => {
										e.stopPropagation();
										onChange(removeSegment(segments, index));
									}}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={2}>
									{inertNotice && (
										<Grid item xs={12}>
											{/* SIMS-9231 AC4 (Hi-Fi badge 17): informational, never the error treatment. */}
											<Alert
												severity="info"
												variant="outlined"
												data-testid={`segment-${index + 1}-inert-threshold`}
												sx={{
													bgcolor: (theme) =>
														alpha(theme.palette.primary.main, 0.06),
													borderColor: (theme) =>
														alpha(theme.palette.primary.main, 0.4),
													color: "text.primary",
													"& .MuiAlert-icon": { color: "primary.main" },
												}}
											>
												{inertNotice}
											</Alert>
										</Grid>
									)}
									{hasContiguityError && (
										<Grid item xs={12}>
											<Alert severity="error">{contiguityError?.message}</Alert>
										</Grid>
									)}
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="Start Week"
											type="number"
											required
											error={hasContiguityError}
											value={segment.weekRange?.start ?? ""}
											onChange={(e) =>
												patchWeek(index, "start", e.target.value)
											}
											// Q-7 (SIMS-9222): per-segment ids by 1-based index, scoped by the editor.
											inputProps={{
												min: 1,
												"data-testid": `segment-${index + 1}-start-week`,
											}}
										/>
									</Grid>
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="End Week"
											type="number"
											required
											value={segment.weekRange?.end ?? ""}
											onChange={(e) => patchWeek(index, "end", e.target.value)}
											inputProps={{
												min: 1,
												"data-testid": `segment-${index + 1}-end-week`,
											}}
										/>
									</Grid>
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="Visits Per Week"
											type="number"
											required
											value={segment.visitsPerWeek ?? ""}
											onChange={(e) =>
												patchNumber(index, "visitsPerWeek", e.target.value)
											}
											inputProps={{
												min: 1,
												"data-testid": `segment-${index + 1}-visits-per-week`,
											}}
										/>
									</Grid>
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="Min Hours Between Visits"
											type="number"
											required
											value={segment.minHoursBetweenVisits ?? ""}
											onChange={(e) =>
												patchNumber(
													index,
													"minHoursBetweenVisits",
													e.target.value,
												)
											}
											inputProps={{
												min: 0,
												"data-testid": `segment-${index + 1}-min-hours`,
											}}
										/>
									</Grid>

									{/*
									 * SIMS-9184 (FR-1, FR-20) — above the Overrides divider and
									 * deliberately outside it: this is NOT an override. It has no
									 * template-level default, so it carries no "Inherits template
									 * default" hint; the caption below explains what it does instead.
									 * Rendered ABSENT, not disabled, on a segment with no successor
									 * (SIMS-9185) — a disabled control still announces itself to a
									 * screen reader as something that could be acted on. Offered on a
									 * plan with several activities too, in the template's and in each
									 * activity's own list (SIMS-9233).
									 */}
									{isNewWeekControlAvailable(segments, index) && (
										<Grid
											item
											xs={12}
											// SIMS-9233 (Q-7): one per segment, so a test can tell the
											// segments' switches apart; the input keeps SIMS-9184's id.
											data-testid={`segment-${index + 1}-new-week`}
										>
											<FormControlLabel
												control={
													<Switch
														size="small"
														inputProps={
															{
																"data-testid": NEW_WEEK_CONTROL_TESTID,
															} as React.InputHTMLAttributes<HTMLInputElement>
														}
														checked={resolveNewWeekValue(segment)}
														onChange={(e) =>
															onChange(
																setNewWeekFlag(
																	segments,
																	index,
																	e.target.checked,
																),
															)
														}
													/>
												}
												label={NEW_WEEK_CONTROL_LABEL}
											/>
											<Typography
												variant="caption"
												color="text.secondary"
												display="block"
											>
												{NEW_WEEK_CONTROL_HINT}
											</Typography>
										</Grid>
									)}

									<Grid item xs={12}>
										<Divider textAlign="left">
											<Typography variant="caption" color="text.secondary">
												Overrides (optional) — off inherits the template default
											</Typography>
										</Divider>
									</Grid>

									{overrideRow(
										index,
										"rewardEngineId",
										"reward engine",
										rewardEngineName(
											templateDefaults.rewardEngineId,
											rewardEngineOptions,
										),
										<FormControl fullWidth size="small">
											<InputLabel>Reward Engine</InputLabel>
											<Select
												label="Reward Engine"
												value={segment.rewardEngineId ?? ""}
												onChange={(e) =>
													onChange(
														setOverride(
															segments,
															index,
															"rewardEngineId",
															e.target.value,
														),
													)
												}
											>
												{rewardEngineOptions.map((engine) => (
													<MenuItem key={engine.id} value={engine.id}>
														{engine.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>,
									)}

									{overrideRow(
										index,
										"behavior",
										"behavior",
										behaviorName(templateDefaults.behavior),
										<FormControl fullWidth size="small">
											<InputLabel>Behavior</InputLabel>
											<Select
												label="Behavior"
												value={segment.behavior ?? ""}
												onChange={(e) =>
													onChange(
														setOverride(
															segments,
															index,
															"behavior",
															e.target.value,
														),
													)
												}
											>
												{behaviors.map((b) => (
													<MenuItem key={b._id} value={b._id}>
														{b.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>,
									)}

									{overrideRow(
										index,
										"cmVisitScheduleOptionIds",
										"visit schedule",
										scheduleDisplay(templateDefaults.cmVisitScheduleOptionIds),
										<FormControl fullWidth size="small">
											<InputLabel>Visit Schedule</InputLabel>
											<Select
												multiple
												label="Visit Schedule"
												value={
													segment.cmVisitScheduleOptionIds
														? segment.cmVisitScheduleOptionIds
																.split(",")
																.filter(Boolean)
														: []
												}
												onChange={(e) =>
													onChange(
														setOverride(
															segments,
															index,
															"cmVisitScheduleOptionIds",
															(e.target.value as string[]).join(","),
														),
													)
												}
												renderValue={(selected) =>
													scheduleDisplay((selected as string[]).join(","))
												}
											>
												{cmVisitScheduleOptions.map((o) => (
													<MenuItem
														key={o.cmVisitScheduleOptions}
														value={o.cmVisitScheduleOptions.toString()}
													>
														{o.cmVisitScheduleOptionsDaysDisplay}
													</MenuItem>
												))}
											</Select>
										</FormControl>,
									)}

									{overrideRow(
										index,
										"disenrollmentDaysThreshold",
										"disenrollment threshold",
										templateDefaults.disenrollmentDaysThreshold != null
											? `${templateDefaults.disenrollmentDaysThreshold} days`
											: "None",
										<TextField
											fullWidth
											size="small"
											label="Disenrollment Days Threshold"
											type="number"
											value={segment.disenrollmentDaysThreshold ?? ""}
											onChange={(e) =>
												onChange(
													setOverride(
														segments,
														index,
														"disenrollmentDaysThreshold",
														e.target.value === "" ? 0 : Number(e.target.value),
													),
												)
											}
											inputProps={{ min: 1 }}
										/>,
									)}

									{/*
									 * SIMS-9226 AC5 (Q-5): the reason a locked switch is locked is persistent text in the
									 * hint slot — the SIMS-7159 hover tooltip was invisible on touch and to keyboard users.
									 * The switch stays disabled (forced on) but only the switch dims; the label keeps its
									 * colour and carries a lock glyph. The reason is the switch's accessible description.
									 */}
									<Grid item xs={12}>
										<FormControlLabel
											sx={{
												"& .MuiFormControlLabel-label.Mui-disabled": {
													color: "text.primary",
												},
											}}
											control={
												<Switch
													size="small"
													checked={resetValue}
													disabled={resetLocked}
													sx={resetLocked ? { opacity: 0.6 } : undefined}
													inputProps={
														{
															"data-testid": `segment-${index + 1}-reset-ladder`,
															"aria-describedby": resetHint.text
																? resetHintId
																: undefined,
														} as React.InputHTMLAttributes<HTMLInputElement>
													}
													onChange={(e) =>
														onChange(
															updateSegment(segments, index, {
																resetLadderOnEntry: e.target.checked,
															}),
														)
													}
												/>
											}
											label={
												<Box
													component="span"
													sx={{
														display: "inline-flex",
														alignItems: "center",
														gap: 0.5,
													}}
												>
													Reset ladder on entry
													{resetLocked && (
														<LockOutlinedIcon
															fontSize="small"
															color="action"
															data-testid={`segment-${index + 1}-reset-ladder-lock`}
														/>
													)}
												</Box>
											}
										/>
										{resetHint.text && (
											<Typography
												id={resetHintId}
												variant="caption"
												color="text.secondary"
												display="block"
												data-testid={`segment-${index + 1}-reset-ladder-hint`}
											>
												{resetHint.text}
											</Typography>
										)}
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>
					);
				})
			)}

			<Box>
				<Button
					size="small"
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={addSegment}
					data-testid="segment-add"
				>
					Add Segment
				</Button>
			</Box>
		</Box>
	);
};

export default SegmentEditor;
